import { Modal } from "@devhop/ui";
import { formatDateLocal } from "@/shared/lib/date-time";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useJDDetailQuery } from "@/modules/jd/presentation/api/queries";
import { JDDetailView } from "@/modules/jd/presentation/ui/JDDetailView";
import type { PositionWithJDDTO } from "@/modules/jd-mapping/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import {
  useCreateMapping,
  useMappingHistoryQuery,
  usePositionsWithJDQuery,
  useUpdateMapping,
} from "../api/queries";
import { JDMappingFilter } from "../ui/JDMappingFilter";
import { JDMappingForm } from "../ui/JDMappingForm";
import { JDMappingHistoryModal } from "../ui/JDMappingHistoryModal";
import { JDMappingTable } from "../ui/JDMappingTable";

export function JDMappingPage() {
  const nav = useNavigate({ from: "/app/jd-mapping" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/jd-mapping" });

  const list = usePositionsWithJDQuery({
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sort: search.sort,
    filters: search.filters,
  });

  const canCreate = useActionPermission(["jd_mapping:create"]);
  const canUpdate = useActionPermission(["jd_mapping:update"]);
  const canManage = canCreate || canUpdate;

  const createMapping = useCreateMapping();
  const mappingModal = useDisclosure<PositionWithJDDTO>();
  const historyModal = useDisclosure<PositionWithJDDTO>();
  const jdDetailModal = useDisclosure<PositionWithJDDTO>();

  const currentPosition = mappingModal.data;
  const updateMapping = useUpdateMapping(currentPosition?.id ?? "");

  const historyQuery = useMappingHistoryQuery(historyModal.data?.id ?? "");

  const selectedPosition = jdDetailModal.data;
  const jdDetailQuery = useJDDetailQuery(
    selectedPosition?.currentMapping?.jdId ?? "",
  );

  const handleSubmit = async (values: { jdId: string; notes?: string }) => {
    if (!currentPosition) return;

    const isUpdate = !!currentPosition.currentMapping;

    if (isUpdate) {
      await updateMapping.mutateAsync({
        jdId: values.jdId,
        notes: values.notes,
      });
    } else {
      await createMapping.mutateAsync({
        jdId: values.jdId,
        positionId: currentPosition.id,
        notes: values.notes,
      });
    }

    mappingModal.close();
  };

  return (
    <>
      <Header />

      <Main>
        <div className="mb-6">
          <h1 className="font-bold text-3xl">ຜູກ JD ກັບຕໍາແໜ່ງ</h1>
          <p className="mt-1 text-muted-foreground">ຈັດການການຜູກ JD ກັບຕໍາແໜ່ງ</p>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <JDMappingFilter />
          <JDMappingTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onEditMapping={(position) => {
              if (canManage) {
                mappingModal.openWith(position);
              }
            }}
            onViewHistory={(position) => {
              historyModal.openWith(position);
            }}
            onViewJD={(position) => {
              jdDetailModal.openWith(position);
            }}
          />
        </div>

        {/* Mapping Form Modal */}
        <Modal
          open={!!mappingModal.isOpen}
          onOpenChange={(open) => {
            if (!open) mappingModal.close();
          }}
          title={
            currentPosition?.currentMapping
              ? "ປ່ຽນ JD ສຳລັບຕໍາແໜ່ງ"
              : "ຜູກ JD ກັບຕໍາແໜ່ງ"
          }
          size="md"
        >
          {currentPosition && (
            <JDMappingForm
              position={currentPosition}
              currentMapping={currentPosition.currentMapping ?? undefined}
              onSubmit={handleSubmit}
              submitting={createMapping.isPending || updateMapping.isPending}
              onCancel={() => mappingModal.close()}
            />
          )}
        </Modal>

        {/* History Modal */}
        <JDMappingHistoryModal
          open={!!historyModal.isOpen}
          onOpenChange={(open) => {
            if (!open) historyModal.close();
          }}
          positionTitle={historyModal.data?.title ?? ""}
          mappings={historyQuery.data?.data ?? []}
          isLoading={historyQuery.isLoading}
        />

        {/* Position & JD Detail Modal */}
        <Modal
          open={!!jdDetailModal.isOpen}
          onOpenChange={(open) => {
            if (!open) jdDetailModal.close();
          }}
          title="ລາຍລະອຽດຕໍາແໜ່ງ ແລະ JD"
          size="lg"
        >
          {!selectedPosition ? (
            <div className="flex items-center justify-center py-8">
              <p className="text-muted-foreground">ບໍ່ພົບຂໍ້ມູນ</p>
            </div>
          ) : (
            <div className="max-h-[70vh] space-y-6 overflow-y-auto">
              {/* Position Information Section */}
              <section className="space-y-3 rounded-lg border bg-card p-4">
                <h2 className="font-semibold text-lg">ຂໍ້ມູນຕໍາແໜ່ງ</h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">ຊື່ຕໍາແໜ່ງ:</span>
                    <p className="font-medium">{selectedPosition.title}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ພະແນກ:</span>
                    <p className="font-medium">
                      {selectedPosition.departmentName || "-"}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ລະດັບ:</span>
                    <p className="font-medium">{selectedPosition.level}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ຈຳນວນຄົນ:</span>
                    <p className="font-medium">
                      {selectedPosition.employeeCount || 0} ຄົນ
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">ສະຖານະ:</span>
                    <p className="font-medium">
                      {selectedPosition.status ? "ໃຊ້ງານ" : "ບໍ່ໃຊ້ງານ"}
                    </p>
                  </div>
                  {selectedPosition.currentMapping && (
                    <div>
                      <span className="text-muted-foreground">ເລີ່ມຜູກ JD:</span>
                      <p className="font-medium">
                        {formatDateLocal(
                          selectedPosition.currentMapping.startDate,
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* JD Information Section */}
              {jdDetailQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <p className="text-muted-foreground">ກຳລັງໂຫຼດ JD...</p>
                </div>
              ) : jdDetailQuery.data ? (
                <JDDetailView
                  jd={jdDetailQuery.data}
                  onEdit={() => {
                    if (selectedPosition.currentMapping?.jdId) {
                      nav({
                        to: `/app/jd/${selectedPosition.currentMapping.jdId}/edit`,
                      });
                    }
                  }}
                  canEdit={false}
                />
              ) : selectedPosition.currentMapping ? (
                <div className="flex items-center justify-center rounded-lg border bg-card p-8">
                  <p className="text-muted-foreground">ບໍ່ພົບ JD</p>
                </div>
              ) : (
                <div className="flex items-center justify-center rounded-lg border bg-card p-8">
                  <p className="text-muted-foreground">ຕໍາແໜ່ງນີ້ຍັງບໍ່ມີ JD ຜູກຢູ່</p>
                </div>
              )}
            </div>
          )}
        </Modal>
      </Main>
    </>
  );
}
